import { useCallback, useMemo, useRef, useState } from 'react'
import type { AddressQuery, House } from '../types/house'
import type { ScanPhase, ScanStep, UsageProfile } from '../types/scan'
import { LookupError, lookupHouse, type LookupErrorKind } from '../utils/bag'
import { computeAdvice, estimateUsage, recommendProduct } from '../utils/energy'
import { products } from '../data/products'
import { demoHouse } from '../data/demoHouse'
import { demoUsage, isDemoQuery } from '../data/demo'

export const SCAN_STEPS: ScanStep[] = ['woning', 'verbruik', 'advies', 'contact']
const MIN_SCAN_MS = 2200

function defaultUsage(house: House | null): UsageProfile {
  const houseType = house?.houseType ?? 'tussenwoning'
  const isApartment = houseType === 'appartement'
  const panels = house && house.panelPotential > 0 ? Math.max(6, Math.min(house.panelPotential, 14)) : 10
  const base = {
    houseType,
    persons: 3,
    hasSolar: !isApartment,
    solarPanels: isApartment ? 6 : panels,
    heatPump: false,
    evCharger: false,
    airco: false,
    contract: 'vast' as const,
    usageEdited: false,
  }
  return { ...base, annualUsage: estimateUsage(base) }
}

const wait = (ms: number) => new Promise((r) => window.setTimeout(r, ms))

export function useHomeScan() {
  const [phase, setPhase] = useState<ScanPhase>('idle')
  const [house, setHouse] = useState<House | null>(null)
  const [errorKind, setErrorKind] = useState<LookupErrorKind | null>(null)
  const [step, setStep] = useState<ScanStep>('woning')
  const [maxStepIndex, setMaxStepIndex] = useState(0)
  const [usage, setUsage] = useState<UsageProfile>(() => defaultUsage(null))
  const [productId, setProductId] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const start = useCallback(async (query: AddressQuery | 'demo') => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setPhase('scanning')
    setErrorKind(null)
    setHouse(null)
    const minDelay = wait(MIN_SCAN_MS)

    try {
      const isDemo = query === 'demo' || isDemoQuery(query)
      const result = isDemo ? demoHouse : await lookupHouse(query, controller.signal)
      await minDelay
      if (controller.signal.aborted) return
      setHouse(result)
      setUsage(isDemo ? { ...demoUsage, annualUsage: estimateUsage(demoUsage) } : defaultUsage(result))
      setProductId(null)
      setStep('woning')
      setMaxStepIndex(0)
      setPhase('result')
    } catch (e) {
      if (controller.signal.aborted) return
      await minDelay
      setErrorKind(e instanceof LookupError ? e.kind : 'network')
      setPhase('error')
    }
  }, [])

  const reset = useCallback(() => {
    abortRef.current?.abort()
    setPhase('idle')
    setHouse(null)
    setErrorKind(null)
  }, [])

  const updateUsage = useCallback((patch: Partial<UsageProfile>) => {
    setUsage((prev) => {
      const next = { ...prev, ...patch }
      if (patch.annualUsage !== undefined) next.usageEdited = true
      else if (!next.usageEdited) next.annualUsage = estimateUsage(next)
      return next
    })
  }, [])

  const goTo = useCallback((s: ScanStep) => {
    setStep(s)
    setMaxStepIndex((m) => Math.max(m, SCAN_STEPS.indexOf(s)))
  }, [])

  const recommended = useMemo(() => recommendProduct(usage, products), [usage])
  const selectedProduct = products.find((p) => p.id === productId) ?? recommended
  const advice = useMemo(() => computeAdvice(usage, selectedProduct), [usage, selectedProduct])

  return {
    phase,
    house,
    errorKind,
    step,
    maxStepIndex,
    usage,
    recommended,
    selectedProduct,
    advice,
    start,
    reset,
    goTo,
    updateUsage,
    setProductId,
  }
}

export type HomeScan = ReturnType<typeof useHomeScan>
